import { useState, useEffect } from "react";
import { format } from "date-fns";
import {
    MapPin,
    ChevronLeft,
    ChevronRight,
    CheckCircle,
    Star,
} from "lucide-react";

export default function HotelDetailsComponent(props) {
    const images = props?.HotelDetails?.img || [];
    const HotelDetail = props?.HotelDetails || [];
    const searchQuery = props?.searchQuery || [];
    const RoomInfos = props?.RoomInfos || [];
    const handleHotelCheckout = props?.handleHotelCheckout;
    const handleScroll = props?.handleScroll;
    const roomRef = props?.roomRef;
    const setShowGuestPopup = props?.setShowGuestPopup;

    const [totalNights, setTotalNights] = useState(0);
    const [totalPax, setTotalPax] = useState(0);
    const [formattedCheckin, setFormattedCheckin] = useState("");
    const [formattedCheckout, setFormattedCheckout] = useState("");

    useEffect(() => {
        // Calculate total nights
        const checkin = new Date(searchQuery.checkinDate);
        const checkout = new Date(searchQuery.checkoutDate);
        const diffTime = checkout - checkin;
        const nights = diffTime / (1000 * 60 * 60 * 24); // convert ms → days
        setTotalNights(nights);

        // Format dates using date-fns
        setFormattedCheckin(format(checkin, "dd MMM yyyy")); // 👉 29 Oct 2025
        setFormattedCheckout(format(checkout, "dd MMM yyyy")); // 👉 30 Oct 2025
        // Calculate total pax (adults + children)
        const pax = searchQuery.roomInfo.reduce(
            (sum, room) => sum + room.numberOfAdults + room.numberOfChild,
            0
        );
        setTotalPax(pax);

    }, []);
    const [current, setCurrent] = useState(0);
    const CurrencySymbol = searchQuery?.searchCriteria?.currency || "INR"
    const nextSlide = () =>
        setCurrent((prev) => (prev + 1) % images.length);
    const prevSlide = () =>
        setCurrent((prev) => (prev - 1 + images.length) % images.length);

    const StarRating = ({ rating }) => {
        if (!rating) return null;

        return (
            <div className="flex">
                {[...Array(5)].map((_, i) => (
                    <svg key={i} className={`w-4 h-4 ${i < rating ? 'text-yellow-400' : 'text-gray-300'}`} fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                ))}
            </div>
        );
    };
    return (
        <>
            <div className="max-w-7xl mx-auto mt-6 bg-white rounded-2xl shadow-md p-4 flex flex-col lg:flex-row gap-4">
                {/* Left Gallery Section */}
                <div className="flex-1 lg:w-[380px]">
                    {/* Main Image with smooth slide effect */}
                    <div className="relative overflow-hidden rounded-xl">
                        <div
                            className="flex transition-transform duration-700 ease-in-out"
                            style={{ transform: `translateX(-${current * 100}%)` }}
                        >
                            {images.map((img, index) => (
                                <img
                                    key={index}
                                    src={img.url}
                                    alt={`Slide ${index}`}
                                    className="w-full h-80 object-cover flex-shrink-0 rounded-xl"
                                />
                            ))}
                        </div>

                        <button
                            onClick={prevSlide}
                            className="absolute left-3 top-1/2 -translate-y-1/2 bg-white p-2 rounded-full shadow hover:bg-gray-100"
                        >
                            <ChevronLeft size={20} />
                        </button>
                        <button
                            onClick={nextSlide}
                            className="absolute right-3 top-1/2 -translate-y-1/2 bg-white p-2 rounded-full shadow hover:bg-gray-100"
                        >
                            <ChevronRight size={20} />
                        </button>
                    </div>

                    {/* Thumbnail images - smooth horizontal scroll */}
                    <div className="flex gap-2 mt-3 overflow-x-auto pb-2 snap-x snap-mandatory scrollbar-hide scroll-smooth">
                        {images.map((img, index) => (
                            <div
                                key={index}
                                className="flex-shrink-0 snap-start"
                                onClick={() => setCurrent(index)}
                            >
                                <img
                                    src={img.url}
                                    className={`h-20 w-32 object-cover rounded-xl cursor-pointer border-2 transition-all duration-300 ease-in-out ${current === index
                                        ? "border-[#f4a406] scale-105"
                                        : "border-transparent hover:opacity-80"
                                        }`}
                                    alt={`Hotel ${index}`}
                                />
                            </div>
                        ))}
                    </div>
                </div>

                {/* Right Content Section */}
                <div className="w-full lg:w-[380px] flex flex-col justify-between border-l border-gray-100 pl-4">
                    {/* Header */}
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900">
                            {HotelDetail?.name}
                        </h2>
                        <div className="flex items-center text-sm text-gray-500 mt-1">
                            <MapPin size={16} className="mr-1 text-gray-400" />
                            {HotelDetail?.ad?.adr}, {HotelDetail?.ad?.adr2} {HotelDetail?.ad?.city?.name} {HotelDetail?.ad?.postalCode}, {HotelDetail?.ad?.cn}
                        </div>

                        {/* Rating */}
                        <div className="flex items-center mt-2">
                            <span className="bg-green-100 text-white text-sm font-semibold px-2 py-1 rounded-md">
                                <StarRating rating={HotelDetail?.rt} />
                            </span>
                            {/* <span className="ml-2 text-sm text-gray-600">
                                Excellent · 350 reviews
                            </span> */}
                        </div>

                        {/* Room Type */}
                        <div className="mt-3">
                            <h3 className="text-lg font-semibold text-gray-800">
                                {RoomInfos && RoomInfos.length > 0 && RoomInfos[0]?.Rooms[0]?.RoomCategory}
                            </h3>
                            <p className="text-sm text-gray-500">{RoomInfos && RoomInfos.length > 0 && RoomInfos[0]?.Rooms[0]?.rooms} Room(s) | {totalPax} Guest | {totalNights} Night(s)</p>
                        </div>

                        {/* Price Section */}
                        <div className="mt-3">
                            <div className="flex items-center gap-2">
                                {/* <span className="text-gray-400 line-through text-sm">₹3,514</span> */}
                                <span className="text-2xl font-bold text-gray-900">{CurrencySymbol} {RoomInfos && Math.ceil(RoomInfos[0]?.Rooms?.[0]?.HotelRoomPrice)}</span>
                            </div>
                            <p className="text-xs text-gray-500">
                                Taxes & fees included
                            </p>
                        </div>
                        <div className="mb-4 p-3 bg-gray-100 rounded-lg mt-4">
                            <div className="flex justify-between items-center mb-1">
                                <span className="text-sm font-medium text-gray-700">CHECKIN : {formattedCheckin}</span>
                                <span className="text-sm font-medium text-gray-700">CHECKOUT : {formattedCheckout}</span>
                            </div>
                        </div>

                        {/* Amenities */}
                        {/* <div className="mt-4 space-y-2 text-sm text-gray-700">
                            {[
                                "Restaurant",
                                "Free Wi-Fi",
                                "24-hour Room Service",
                                "Gym",
                                "Free Toiletries",
                            ].map((item, i) => (
                                <div key={i} className="flex items-center gap-2">
                                    <CheckCircle size={16} className="text-green-500" />
                                    {item}
                                </div>
                            ))}
                        </div> */}
                    </div>

                    {/* Buttons */}
                    <div className="mt-6 flex items-center gap-3 w-full grid grid-cols-2">
                        <button onClick={() => handleHotelCheckout(RoomInfos && RoomInfos[0])} className="bg-blue-500 text-white font-medium py-2 rounded-lg hover:bg-blue-600 transition">
                            Book Now
                        </button>
                        <button onClick={() => setShowGuestPopup(true)} className="border border-blue-500  font-medium py-2 rounded-lg hover:bg-blue-50 transition">
                            Share
                        </button>

                        {/* <div className="text-sm text-center bg-yellow-50 text-gray-700 rounded-lg py-2">
                            Flat ₹500 Cashback on booking above ₹5,000
                        </div> */}
                    </div>
                    <div className="mt-6 flex flex-col gap-3">
                        <button onClick={() => handleScroll(roomRef, 'rooms')} className="border border-blue-500 text-blue-500 font-medium py-2 rounded-lg hover:bg-blue-500 hover:text-white transition">
                            Select Rooms
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
}
